/**
 * Polymarket slug codes -> the ESPN abbreviations stored results carry.
 *
 * **This is the standing limitation of the propensity model.** The two feeds disagree on about a
 * third of MLS clubs (`nyr` vs `RBNY`, `fcc` vs `CIN`) and on most of USL Championship, and a
 * fixture needs BOTH sides resolved to be scored — so without this map roughly two thirds of
 * matches get no signal at all.
 *
 * **An unmapped code is left unmapped rather than guessed.** A wrong entry silently attributes one
 * club's scoring history to another and quietly corrupts a league baseline, which is strictly worse
 * than a fixture showing no signal — and in the trader's Safe mode, no signal means no bet. Three
 * filters produced the derived half:
 *
 * 1. Hand-verified entries always win; a name-matching guess never overrides one. That is what kept
 *    Brazil's `cre` on Cruzeiro when name-matching claimed Remo.
 * 2. The target abbreviation must already appear in stored results for that league, so an entry can
 *    never point at a club with no history.
 * 3. If two codes resolved to the same club, BOTH were dropped — that ambiguity is how Peru's `ctd`
 *    and `fcc` both landed on CAJ (UTC Cajamarca vs FC Cajamarca).
 *
 * Cup competitions resolve poorly here on purpose: ESPN's team list for a cup covers only that
 * season's entrants, and a club's history in a cup is a handful of matches regardless.
 *
 * The JSON beside this file is lifted mechanically from the C# maps by
 * `tools/extract-team-codes.mjs` — 138 entries, 29 of them hand-verified. Retyping them is exactly
 * how a wrong one gets in.
 */
import codes from './teamCodes.generated.json';

interface CodeEntry {
  espn: string;
  name: string;
  verified: boolean;
}

const BY_LEAGUE = codes as Record<string, Record<string, CodeEntry>>;

/** Maps a Polymarket code to its ESPN equivalent, or returns it unchanged when they agree. */
export function toEspnCode(leagueCode: string, polymarketCode: string): string {
  if (!polymarketCode?.trim()) return polymarketCode;
  const code = polymarketCode.trim().toLowerCase();
  return BY_LEAGUE[leagueCode?.toLowerCase()]?.[code]?.espn ?? polymarketCode.trim();
}

/** How many of a league's codes can be translated — surfaced so the gaps stay visible. */
export const mappedCount = (leagueCode: string) =>
  Object.keys(BY_LEAGUE[leagueCode?.toLowerCase()] ?? {}).length;

export const codeEntries = (leagueCode: string) => BY_LEAGUE[leagueCode?.toLowerCase()] ?? {};
